import {Box, createStyles, MultiSelect} from '@mantine/core';
import React, {useState} from 'react';

import {BORDER_RADIUS_DEFAULT} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';

import PrimitiveTagsInputTag from './PrimitiveTagsInputTag';

// Match SelectDropdown btn-af-default sizing in modals (@input-height-base = 34px).
const SELECT_DROPDOWN_HEIGHT_PX = 28;

const useStyles = createStyles((theme, params: {hasError: boolean; hasPendingSearchText: boolean}) => ({
  input: {
    minHeight: `${SELECT_DROPDOWN_HEIGHT_PX}px`,
    paddingTop: '2px',
    paddingBottom: '2px',
    paddingLeft: '10px',
    paddingRight: '10px',
    borderRadius: BORDER_RADIUS_DEFAULT,
    borderColor: params.hasError ? theme.colors.red[6] : theme.colors.gray[3],
  },
  error: {
    display: 'none',
  },
  // This is a free-form tag entry, not a selection control, so there's no list of options to browse: values are
  // committed by typing and pressing enter. Force the dropdown off rather than relying on internal Mantine state
  // (e.g. `creatable`/`dropdownOpened`), which can still flip to "open" as a side effect of keyboard handling.
  dropdown: {
    display: 'none',
  },
  // Mantine collapses the search input to zero width whenever its internal `dropdownOpened` state is false and
  // there's at least one tag already committed — including on blur — which hides any typed-but-not-yet-committed
  // text as soon as the user clicks away. Override it back to full width, but only while there's actually pending
  // text to show: keeping it expanded unconditionally reserves layout space even when empty, which makes the
  // control visibly grow/wrap once tags fill more than half its width.
  searchInputInputHidden: params.hasPendingSearchText
    ? {
        flex: 1,
        width: 'auto',
        minWidth: '60px',
        marginLeft: theme.spacing.xxs,
        overflow: 'visible',
      }
    : {},
}));

export interface PrimitiveTagsInputProps {
  /** The current list of entered values. */
  value: Array<string>;
  onChange: (value: Array<string>) => void;
  /** Defaults to "Type a value then press enter to add". Shown only while no tags are committed. */
  placeholder?: string;
  disabled?: boolean;
  error?: React.ReactNode;
  className?: string;
  /**
   * Return false to reject a typed token before it becomes a tag (e.g. non-numeric input). Receives the
   * trimmed query. Defaults to accepting any non-empty, non-duplicate token.
   */
  shouldCreateItem?: (query: string) => boolean;
  dataTestAttrs?: DataTestAttrs;
}

// TODO(BNCH-163953) Replace with Mantine v8 TagsInput
/**
 * A standard input for entering a free-form list of primitive (string) values, rendered as removable tags.
 *
 * This wraps Mantine v6 `MultiSelect` (the dedicated `TagsInput` component only exists in Mantine v7+, which
 * this repo does not use) but suppresses its dropdown entirely: there's no fixed set of options to browse, so
 * typing a value and pressing enter commits it directly as a tag. Callers that need typed (e.g. numeric)
 * values should validate via `shouldCreateItem` and convert the resulting strings themselves.
 */
export default function PrimitiveTagsInput(props: PrimitiveTagsInputProps): JSX.Element {
  // Mantine requires every selected value to also exist in `data`; track created options here and union with
  // the controlled value so tags loaded from persisted state still render.
  const [createdOptions, setCreatedOptions] = useState<Array<string>>(() => [...props.value]);
  const [searchValue, setSearchValue] = useState('');

  // Surface a red error border (mirroring the numeric input) whenever the pending typed text can't be committed
  // because it fails the caller's type check — e.g. a decimal typed into an integer-only list.
  const trimmedSearch = searchValue.trim();
  const hasInvalidPendingValue =
    trimmedSearch.length > 0 && props.shouldCreateItem?.(trimmedSearch) === false;
  const showError = Boolean(props.error) || hasInvalidPendingValue;

  const {classes, theme} = useStyles({hasError: showError, hasPendingSearchText: searchValue.length > 0});

  const data = Array.from(new Set([...createdOptions, ...props.value]));

  const commitSearchValue = (): void => {
    const trimmed = searchValue.trim();
    if (trimmed.length === 0 || props.value.includes(trimmed)) {
      return;
    }
    if (!(props.shouldCreateItem?.(trimmed) ?? true)) {
      // Leave the rejected text in place so the user can see and correct it.
      return;
    }
    setCreatedOptions((current) => (current.includes(trimmed) ? current : [...current, trimmed]));
    props.onChange([...props.value, trimmed]);
    setSearchValue('');
  };

  return (
    // Mantine spreads unrecognized props (including data-testid) onto its inner search input, so wrap in a Box
    // to give the whole control — input plus rendered tags — a single stable root for querying. Reflect the
    // error state onto the root (Mantine only sets `data-invalid` on an inner, role-less element) so it's
    // queryable.
    <Box
      className={props.className}
      data-invalid={showError ? 'true' : undefined}
      {...getTestProps(props.dataTestAttrs)}
    >
      <MultiSelect
        size="sm"
        // `searchInputInputHidden` is deliberately excluded from Mantine's public `classNames` type (it's meant
        // to be an internal styling detail), but the underlying `createStyles`-based merging still honors any
        // key present in the styles hook at runtime, so cast to override it. See the `searchInputInputHidden`
        // style definition above for why this override is needed.
        classNames={
          {
            input: classes.input,
            error: classes.error,
            dropdown: classes.dropdown,
            searchInputInputHidden: classes.searchInputInputHidden,
          } as Partial<Record<string, string>>
        }
        styles={{
          values: {minHeight: `${SELECT_DROPDOWN_HEIGHT_PX - 4}px`, alignItems: 'center'},
          searchInput: {fontSize: theme.fontSizes.md, lineHeight: '20px'},
        }}
        data={data}
        value={props.value}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        placeholder={props.placeholder ?? 'Type a value then press enter to add'}
        disabled={props.disabled}
        error={showError || undefined}
        searchable={true}
        clearable={false}
        // Renders each committed tag ourselves (instead of Mantine's default) so a long, truncated value can
        // be wrapped in a tooltip revealing its full text on hover.
        valueComponent={PrimitiveTagsInputTag}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commitSearchValue();
          }
        }}
        onChange={props.onChange}
      />
    </Box>
  );
}
